/**
 * React hooks for sync functionality.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { syncService, type SyncStatus } from '@/lib/sync-service';
import { statsCache, type CountryStats } from '@/lib/stats-cache';

// Re-export CountryStats type for consumers
export type { CountryStats };

// API base URL for direct fetches (leaderboard, summary)
const API_BASE = import.meta.env.VITE_SYNC_API_URL ||
  import.meta.env.VITE_PHOTO_API_URL ||
  'https://where-in-the-world-photos.armincerf.workers.dev';

/**
 * Hook to track sync status.
 * Returns current sync state, pending count, and error info.
 */
export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(() => syncService.getStatus());

  useEffect(() => {
    return syncService.subscribe(setStatus);
  }, []);

  return status;
}

/**
 * Hook to trigger manual sync.
 */
export function useSyncNow(): () => Promise<boolean> {
  return useCallback(() => syncService.syncNow(), []);
}

interface GlobalStatsState {
  stats: Record<string, CountryStats>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch global stats for countries.
 * Uses shared cache with automatic batching to prevent N+1 fetches.
 *
 * @param countryCodes - Optional list of country codes to fetch.
 *   If not provided, no stats are fetched.
 */
export function useGlobalStats(countryCodes?: string[]): GlobalStatsState & { refetch: () => void } {
  const [state, setState] = useState<GlobalStatsState>({
    stats: {},
    loading: !!countryCodes?.length,
    error: null,
  });

  // Track mounted state to avoid state updates after unmount
  const isMounted = useRef(true);

  const fetchStats = useCallback(async () => {
    if (!countryCodes || countryCodes.length === 0) {
      setState({ stats: {}, loading: false, error: null });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Use shared cache - automatically batches requests
      const stats = await statsCache.getStats(countryCodes);

      if (isMounted.current) {
        setState({
          stats,
          loading: false,
          error: null,
        });
      }
    } catch (err) {
      console.error('Failed to load global stats:', err);
      if (isMounted.current) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    }
  }, [countryCodes?.join(',')]);

  useEffect(() => {
    isMounted.current = true;
    fetchStats();

    return () => {
      isMounted.current = false;
    };
  }, [fetchStats]);

  return { ...state, refetch: fetchStats };
}

export interface LeaderboardEntry {
  rank: number;
  countryCode: string;
  totalVotes: number;
  avgRating: number;
}

interface LeaderboardState {
  leaderboard: LeaderboardEntry[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch global leaderboard.
 */
export function useLeaderboard(limit = 20, minVotes = 5): LeaderboardState & { refetch: () => void } {
  const [state, setState] = useState<LeaderboardState>({
    leaderboard: [],
    loading: true,
    error: null,
  });

  const fetchLeaderboard = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const url = `${API_BASE}/api/stats/leaderboard?limit=${limit}&minVotes=${minVotes}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch leaderboard: ${response.status}`);
      }

      const data = await response.json();
      setState({
        leaderboard: data.leaderboard || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
    }
  }, [limit, minVotes]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return { ...state, refetch: fetchLeaderboard };
}

export interface StatsSummary {
  totalUsers: number;
  totalRatings: number;
  countriesRated: number;
}

interface SummaryState {
  summary: StatsSummary | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch overall stats summary.
 */
export function useStatsSummary(): SummaryState & { refetch: () => void } {
  const [state, setState] = useState<SummaryState>({
    summary: null,
    loading: true,
    error: null,
  });

  const fetchSummary = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch(`${API_BASE}/api/stats/summary`);
      if (!response.ok) {
        throw new Error(`Failed to fetch summary: ${response.status}`);
      }

      const data = await response.json();
      setState({
        summary: data.summary || null,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Failed to load stats summary:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { ...state, refetch: fetchSummary };
}
